import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { localToLonLat, lonLatDistanceM, lonLatToLocal, syncTargetDecision } from '../lib/view-sync';

const CAMERA_EPS_M = 1;

/**
 * 3D side of the view sync (docs/TODO.md 3.7). Renders inside the <Canvas>; all state comes in
 * as props (React contexts are read outside the canvas by SubsurfaceViewer3D).
 *
 *  - highlight sphere at the hover coming from the map / section
 *  - the current section line as a thin vertical plane outline
 *  - camera sync: orbit target <-> map centre through the scene's lon/lat origin (the same
 *    georef-derived origin the fence planes and voxel clouds use). A map target further than
 *    SYNC_FAR_M from the models' `extent` is not followed: `onFit()` frames the models instead
 *    (docs/TODO.md P.20 option a) and returns the new orbit target, which is not echoed to the map.
 */
export default function ViewSync3D({ origin, hovered, sectionLine, sectionDepth = 50, syncCamera, cameraState, publishCamera, targetRef, markerSize = 2, extent = null, onFit = null }) {
  // ── Hover highlight ──
  const spherePos = useMemo(() => {
    if (!origin || !hovered || hovered.source === '3d' || !hovered.lonLat) return null;
    const [x, z] = lonLatToLocal(hovered.lonLat, origin);
    return [x, typeof hovered.depth === 'number' ? -hovered.depth : 0, z];
  }, [origin, hovered]);

  // ── Section line outline ──
  const outline = useMemo(() => {
    const c = sectionLine?.coordinates;
    if (!origin || !c || c.length < 2) return null;
    const pts = c.map((p) => lonLatToLocal(p, origin));
    const v = [];
    for (let i = 1; i < pts.length; i++) {
      const [ax, az] = pts[i - 1], [bx, bz] = pts[i];
      v.push(ax, 0, az, bx, 0, bz, ax, -sectionDepth, az, bx, -sectionDepth, bz);
    }
    const [fx, fz] = pts[0], [lx, lz] = pts[pts.length - 1];
    v.push(fx, 0, fz, fx, -sectionDepth, fz, lx, 0, lz, lx, -sectionDepth, lz);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
    return g;
  }, [origin, sectionLine, sectionDepth]);
  useEffect(() => () => { if (outline) outline.dispose(); }, [outline]);

  // ── Camera sync ──
  const { camera } = useThree();
  const last = useRef(null);
  const published = useRef(0);
  const far = useRef(false);      // the map target is far from the models and the 3D view fits them
  const fitHold = useRef(null);   // lon/lat of the fitted target: not published back until the user orbits

  useFrame(() => {
    if (!syncCamera || !origin || !targetRef?.current) return;
    const t = targetRef.current;
    const lonLat = localToLonLat(t.x, t.z, origin);
    if (fitHold.current) {
      if (lonLatDistanceM(lonLat, fitHold.current) < CAMERA_EPS_M) return;
      fitHold.current = null;
    }
    if (lonLatDistanceM(lonLat, last.current) < CAMERA_EPS_M) return;
    const now = performance.now();
    if (now - published.current < 100) return;
    published.current = now;
    last.current = lonLat;
    publishCamera({ lonLat, source: '3d' });
  });

  useEffect(() => {
    if (!syncCamera || !origin || !cameraState || cameraState.source === '3d' || !targetRef?.current) return;
    if (syncTargetDecision(cameraState.lonLat, extent) === 'fit') {
      if (far.current) return; // already framing the models: keep them while the map stays far away
      far.current = true;
      last.current = cameraState.lonLat;
      const fitted = onFit ? onFit() : null;
      if (fitted) fitHold.current = localToLonLat(fitted[0], fitted[2], origin);
      return;
    }
    if (lonLatDistanceM(cameraState.lonLat, last.current) < CAMERA_EPS_M) return;
    last.current = cameraState.lonLat;
    far.current = false;
    fitHold.current = null;
    const [x, z] = lonLatToLocal(cameraState.lonLat, origin);
    const t = targetRef.current;
    const delta = new THREE.Vector3(x - t.x, 0, z - t.z);
    t.add(delta);
    // Translate the camera with the target so the orbit keeps its shape
    camera.position.add(delta);
  }, [syncCamera, origin, cameraState, targetRef, camera, extent, onFit]);

  return (
    <>
      {spherePos && (
        <mesh position={spherePos}>
          <sphereGeometry args={[markerSize, 16, 12]} />
          <meshBasicMaterial color="#f472b6" depthTest={false} transparent opacity={0.9} />
        </mesh>
      )}
      {outline && (
        <lineSegments geometry={outline}>
          <lineBasicMaterial color="#f472b6" />
        </lineSegments>
      )}
    </>
  );
}
